import { AppProps } from "next/app";
import { Provider } from "react-redux";
import { EmotionCache } from "@emotion/react";
import { ElementType, ReactNode } from "react";

import store from "../store/store";
import { AuthProvider } from "./Auth";
import { GlobalModals } from "./GlobalModals";
import { HeaderScripts } from "./HeaderScripts";
import { FaviconProvider } from "./FaviconProvider";
import { AppThemeProvider } from "./AppThemeProvider";
import { RedirectProvider } from "./RedirectProvider";
import { IntercomCtxProvider } from "./IntercomProvider";
import { FraudPreventionProvider } from "./FraudPreventionProvider";
import { OktaProvider } from "@/features/oAuth/provider/OktaProvider";
import { GlobalQueryClientProvider } from "./GlobalQueryClientProvider";
import { RepInfoCheckerContainer } from "./RepInfoCheckerContainer";
import { AppCloseCancelCartHandler } from "./AppCloseCancelCartHandler";
import { MiddlewareIoHeaderContainer } from "./MiddlewareIoHeaderContainer";
import { GlobalEventListenerContainer } from "./GlobalEventListenerContainer";
import { CartCacheProvider } from "./CartCacheProvider";
import { InfoFieldsContainer } from "./InfoFieldsContainer";
import { I18nProvider } from "./I18nProvider";

export interface MyAppProps extends AppProps {
  emotionCache?: EmotionCache;
}

export const BaseAppProvider = (props: { children: ReactNode }) => (
  <Provider store={store}>{props.children}</Provider>
);

export const withTeamConfig = (Component: ElementType) => {
  const Wrapped = (props: MyAppProps) => (
    <>
      <MiddlewareIoHeaderContainer />
      <Provider store={store}>
        <GlobalQueryClientProvider>
          <AppThemeProvider>
            <I18nProvider>
              <OktaProvider>
                <FaviconProvider />
                <HeaderScripts />
                <GlobalEventListenerContainer />
                <AuthProvider>
                  <IntercomCtxProvider>
                    <FraudPreventionProvider>
                      <RedirectProvider>
                        <RepInfoCheckerContainer />
                        <InfoFieldsContainer />
                        <GlobalModals />
                        <AppCloseCancelCartHandler />
                        <CartCacheProvider>
                          <Component {...props} />
                        </CartCacheProvider>
                      </RedirectProvider>
                    </FraudPreventionProvider>
                  </IntercomCtxProvider>
                </AuthProvider>
              </OktaProvider>
            </I18nProvider>
          </AppThemeProvider>
        </GlobalQueryClientProvider>
      </Provider>
    </>
  );

  return Wrapped;
};
